<template>
    <div>
        <div v-if="progress > 0 && progress < 100"
             class="inline-flex items-center rounded-sm bg-gray-700 dark:bg-gray-500 px-4 w-70 max-w-full py-3">
            <div class="flex items-center gap-2.5 w-full">
                <div class="w-5.5 h-5.5 border-[3px] border-white/30 border-t-white rounded-full animate-spin"></div>
                <div class="w-2 h-2 rounded-full bg-white dark:bg-black/40 flex-shrink-0"></div>
                <div class="flex-1 h-1 bg-white dark:bg-black/20 rounded-sm"></div>
                <span
                    class="text-xs text-white dark:text-black/60 font-mono whitespace-nowrap flex-shrink-0">0:00</span>
            </div>
        </div>
        <div v-else class="rounded-sm overflow-hidden dark:bg-gray-700 bg-gray-200">
            <audio
                ref="audioEl"
                controls
                preload="metadata"
                playsinline
                webkit-playsinline="true"
                class="w-70 max-w-full block"
                @loadedmetadata="handleLoadedMetadata"
                @durationchange="handleDurationChange"
                @timeupdate="handleTimeUpdate"
                @canplay="clearWatchdog"
                @error="handleError"
            >
                <source :src="url" :type="mimetype">
            </audio>
            <!-- O controle nativo do WebKit (iOS) não exibe tempo tocado/total
                 como o do Chromium (Android). Mostramos nosso próprio label
                 só nessa plataforma pra não duplicar a informação no Android. -->
            <div
                v-if="showCustomTime"
                class="flex justify-between px-2.5 pb-1.5 -mt-1 text-[11px] font-mono text-black/50 dark:text-white/60 select-none"
            >
                <span>{{ formatTime(currentTime) }}</span>
                <span>{{ formatTime(totalDuration) }}</span>
            </div>
            <div
                v-if="isWaitingForFile"
                class="px-2.5 pb-1.5 -mt-1 text-[11px] text-black/50 dark:text-white/60 select-none"
            >
                Carregando áudio…
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import {computed, onMounted, onUnmounted, ref} from "vue";
import {Capacitor} from "@capacitor/core";
import {useChatSessionStore} from "@src/stores/chatSession";
import {useMessageStore} from "@src/stores/messages";

const props = defineProps(['data', 'preview', 'id'])
const url = ref<string>('')
const mimetype = ref<string>('')
const audioEl = ref<HTMLAudioElement | null>(null)
const sessionStore = useChatSessionStore()

// O controle nativo <audio controls> do WebKit (iOS) não traz tempo tocado
// nem duração, diferente do Chromium (Android), que já mostra isso embutido
// no próprio widget. Então montamos esse label só pra essa engine.
const showCustomTime = Capacitor.getPlatform() === 'ios'
const currentTime = ref(0)
const totalDuration = ref(0)

function formatTime(seconds: number): string {
    if (!isFinite(seconds) || seconds < 0) return '00:00'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

function handleTimeUpdate(): void {
    const el = audioEl.value
    if (!el) return
    currentTime.value = el.currentTime
}

function handleDurationChange(): void {
    const el = audioEl.value
    if (!el || !isFinite(el.duration)) return
    totalDuration.value = el.duration
}

// Áudios gravados no app (mp3 via recorder-js) às vezes não trazem a
// duração no cabeçalho, e o Chromium pode deixar a posição de reprodução
// no fim do arquivo enquanto tenta descobri-la. Só resetamos quando a
// duração já é conhecida — forçar um seek pro fim pra "descobrir" a
// duração depende do servidor suportar Range requests e, quando não
// suporta, trava o player numa busca que nunca termina.
function fixDuration(): void {
    const el = audioEl.value
    if (!el) return

    if (isFinite(el.duration) && el.duration > 0 && el.currentTime !== 0) {
        el.currentTime = 0
    }
    totalDuration.value = isFinite(el.duration) ? el.duration : totalDuration.value
}

// Logo após enviar, o arquivo às vezes ainda não terminou de ser
// gravado/propagado na URL devolvida pelo servidor. Nesse caso o LiteSpeed
// responde a requisição de Range com 412 e corpo vazio, o que o Chromium
// não trata como um erro de mídia (o evento "error" nunca dispara) — o
// player só fica parado, sem metadata, pra sempre. Por isso o retry usa um
// watchdog por tempo (loadedmetadata nunca chegou) em vez de depender só
// do evento de erro.
const MAX_LOAD_ATTEMPTS = 5
const RETRY_INTERVAL_MS = 1500
const loadAttempts = ref(0)
const isWaitingForFile = ref(false)
let watchdogTimer: ReturnType<typeof setTimeout> | null = null

function clearWatchdog(): void {
    isWaitingForFile.value = false
    if (watchdogTimer) {
        clearTimeout(watchdogTimer)
        watchdogTimer = null
    }
}

function retryLoad(reason: string): void {
    const el = audioEl.value
    if (!el || loadAttempts.value >= MAX_LOAD_ATTEMPTS) {
        isWaitingForFile.value = false
        return
    }

    loadAttempts.value++
    console.warn(`[audio] retrying load (${reason}), attempt ${loadAttempts.value}/${MAX_LOAD_ATTEMPTS} url=${url.value}`)
    el.load()
    scheduleWatchdog()
}

function scheduleWatchdog(): void {
    clearWatchdog()
    if (loadAttempts.value >= MAX_LOAD_ATTEMPTS) return
    isWaitingForFile.value = true
    watchdogTimer = setTimeout(() => retryLoad('timeout'), RETRY_INTERVAL_MS)
}

function handleLoadedMetadata(): void {
    clearWatchdog()
    fixDuration()
}

function handleError(): void {
    const el = audioEl.value
    console.error(
        `[audio] player failed to load: code=${el?.error?.code} message=${el?.error?.message} url=${url.value}`
    )
    retryLoad('error')
}

const progress = computed(() => {
    const messageStore = useMessageStore()
    const message = sessionStore.type === 'session'
        ? sessionStore.selectedSession?.messages?.find(msg => msg.id === props.id)
        : messageStore.groupMessages?.find(msg => msg.id === props.id);
    return message?.progress || 0;
})
const getMimeType = (fileUrl: string) => {
    const urlParts = fileUrl.split('/')
    const fileName = urlParts[urlParts.length - 1]
    const cleanFileName = decodeURIComponent(fileName.split('?')[0])
    const extension = cleanFileName.split('.').pop()?.toLowerCase()
    if (extension) {
        mimetype.value = getMimetypeByExtension(extension)
    }
}

function getMimetypeByExtension(extension: string): string {
    const extensionMap: Record<string, string> = {
        'mp3': 'audio/mpeg',
        'wav': 'audio/wav',
        'amr': 'audio/amr',
        'm4a': 'audio/m4a',
        'mp4': 'audio/mp4'
    }

    return extensionMap[extension] || 'audio/mpeg'
}

onMounted(() => {
    try {
        const parsed = JSON.parse(props.data)
        url.value = parsed.url
    } catch (e) {
        url.value = props.data
    }
    getMimeType(url.value)
    scheduleWatchdog()
})

onUnmounted(() => {
    clearWatchdog()
})
</script>
